import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faUser, faEnvelope, faLock, faPhone, faUserPlus, 
    faEye, faEyeSlash, faCheckCircle, faExclamationTriangle 
} from '@fortawesome/free-solid-svg-icons';
import { API_BASE } from './apiConfig';

const Register = () => {
    const { t } = useTranslation();
    const [formData, setFormData] = useState({ 
        full_name: '', 
        email: '', 
        password: '', 
        confirmPassword: '',
        phone: '' 
    });
    
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [isFormValid, setIsFormValid] = useState(false);
    const navigate = useNavigate();

    const checkPasswordStrength = (pass) => {
        return {
            minLength: pass.length >= 8,
            hasNumber: /\d/.test(pass),
            hasUpper: /[A-Z]/.test(pass)
        };
    };

    const strength = checkPasswordStrength(formData.password);

    useEffect(() => {
        const { full_name, email, password, confirmPassword, phone } = formData;
        const isValid = 
            full_name.trim().length >= 3 &&
            email.includes('@') &&
            phone.replace(/\D/g, '').length >= 9 &&
            password === confirmPassword &&
            strength.minLength && strength.hasNumber && strength.hasUpper;
        
        setIsFormValid(isValid);
    }, [formData, strength]);

    const handleRegister = async (e) => {
        e.preventDefault();
        if (!isFormValid) return;

        setLoading(true);
        setError('');

        try {
            const res = await fetch(`${API_BASE}/api/auth/register`, { 
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    'ngrok-skip-browser-warning': 'true' 
                },
                body: JSON.stringify({
                    full_name: formData.full_name,
                    email: formData.email,
                    phone: formData.phone,
                    password: formData.password,
                    specializations: [] 
                })
            });
            const data = await res.json();

            if (res.ok) {
                alert(t('register_success_alert'));
                navigate('/user-login');
            } else {
                setError(data.error || t('register_error_generic'));
            }
        } catch (err) {
            setError(t('error_server_unavailable'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="register-page-wrapper"> {/* ✅ Փոխված կլաս Navbar-ի հետ չխփելու համար */}
            <Helmet><title>{t('register_page_title')} | LIBER GROUP</title></Helmet>
            
            {/* ✅ Միացնում ենք CSS-ը */}
            <style>{premiumStyles}</style>

            <div className="register-container">
                <div className="register-card">
                    <div className="register-header">
                        <div className="logo-circle-reg">
                            <FontAwesomeIcon icon={faUserPlus} />
                        </div>
                        <h1>LIBER <span>GROUP</span></h1>
                        <p>{t('register_subtitle')}</p>
                    </div>

                    {error && <div className="register-error animate-shake">{error}</div>}

                    <form onSubmit={handleRegister} className="register-form">
                        <div className="field-group">
                            <label>{t('form_full_name')}</label>
                            <div className="input-box">
                                <FontAwesomeIcon icon={faUser} className="input-icon" />
                                <input 
                                    type="text" placeholder={t('placeholder_name')} required
                                    value={formData.full_name}
                                    onChange={e => setFormData({...formData, full_name: e.target.value})}
                                />
                            </div>
                        </div>

                        <div className="field-group">
                            <label>{t('form_email')}</label>
                            <div className="input-box">
                                <FontAwesomeIcon icon={faEnvelope} className="input-icon" />
                                <input 
                                    type="email" placeholder="example@mail.com" required
                                    value={formData.email}
                                    onChange={e => setFormData({...formData, email: e.target.value})}
                                />
                            </div>
                        </div>

                        <div className="field-group">
                            <label>{t('form_phone')}</label>
                            <div className="input-box">
                                <FontAwesomeIcon icon={faPhone} className="input-icon" />
                                <input 
                                    type="text" placeholder="094XXXXXX" required
                                    value={formData.phone}
                                    onChange={e => setFormData({...formData, phone: e.target.value})}
                                />
                            </div>
                        </div>

                        <div className="field-group">
                            <label>{t('form_password')}</label>
                            <div className="input-box">
                                <FontAwesomeIcon icon={faLock} className="input-icon" />
                                <input 
                                    type={showPassword ? "text" : "password"} 
                                    placeholder="••••••••" required
                                    value={formData.password}
                                    onChange={e => setFormData({...formData, password: e.target.value})}
                                />
                                <button type="button" className="eye-btn" onClick={() => setShowPassword(!showPassword)}>
                                    <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                                </button>
                            </div>
                            <div className="password-hints">
                                <span className={strength.minLength ? 'valid' : ''}>{t('pass_hint_chars')}</span>
                                <span className={strength.hasNumber ? 'valid' : ''}>{t('pass_hint_number')}</span>
                                <span className={strength.hasUpper ? 'valid' : ''}>{t('pass_hint_upper')}</span>
                            </div>
                        </div>

                        <div className="field-group">
                            <label>{t('form_confirm_password')}</label>
                            <div className="input-box">
                                <FontAwesomeIcon icon={faLock} className="input-icon" />
                                <input 
                                    type={showPassword ? "text" : "password"} 
                                    placeholder="••••••••" required
                                    value={formData.confirmPassword}
                                    onChange={e => setFormData({...formData, confirmPassword: e.target.value})}
                                />
                            </div>
                            {formData.confirmPassword && formData.password !== formData.confirmPassword && (
                                <span className="mismatch-text">
                                    <FontAwesomeIcon icon={faExclamationTriangle} /> {t('error_pass_mismatch')}
                                </span>
                            )}
                        </div>

                        <button 
                            type="submit" 
                            className="reg-submit-btn" 
                            disabled={loading || !isFormValid}
                        >
                            {loading ? <div className="spinner"></div> : <>{t('btn_create_account')} <FontAwesomeIcon icon={faCheckCircle} /></>}
                        </button>
                    </form>

                    <div className="register-footer">
                        <p>{t('already_have_account')} <Link to="/user-login">{t('login_link')}</Link></p>
                    </div>
                </div>
            </div>
        </div>
    );
};

const premiumStyles = `
    @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;600;700&display=swap');

    .register-page-wrapper {
        min-height: 100vh;
        background: #0f111a;
        background-image: radial-gradient(at 0% 0%, rgba(16, 185, 129, 0.08) 0, transparent 50%), radial-gradient(at 100% 100%, rgba(5, 150, 105, 0.08) 0, transparent 50%);
        display: flex; align-items: center; justify-content: center; padding: 40px 20px;
        font-family: 'Plus Jakarta Sans', sans-serif;
    }

    .register-container { width: 100%; max-width: 480px; animation: slideUpReg 0.6s ease-out; }
    @keyframes slideUpReg { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }

    .register-card {
        background: rgba(30, 41, 59, 0.4);
        backdrop-filter: blur(15px); -webkit-backdrop-filter: blur(15px);
        border: 1px solid rgba(255, 255, 255, 0.1);
        border-radius: 32px; padding: 40px; box-shadow: 0 40px 100px rgba(0, 0, 0, 0.4);
    }

    .register-header { text-align: center; margin-bottom: 25px; }
    .logo-circle-reg {
        width: 60px; height: 60px; background: linear-gradient(135deg, #10b981, #059669);
        border-radius: 18px; display: flex; align-items: center; justify-content: center;
        margin: 0 auto 15px; font-size: 26px; color: #fff; box-shadow: 0 10px 20px rgba(16, 185, 129, 0.2);
    }

    .register-header h1 { color: #fff; font-size: 24px; font-weight: 700; margin: 0; }
    .register-header h1 span { color: #10b981; }
    .register-header p { color: #94a3b8; font-size: 14px; margin-top: 5px; }

    .register-error {
        background: rgba(244, 63, 94, 0.15); color: #fb7185; padding: 12px;
        border-radius: 12px; font-size: 13px; margin-bottom: 20px; text-align: center;
        border: 1px solid rgba(244, 63, 94, 0.2);
    }

    .register-form { display: flex; flex-direction: column; gap: 16px; }
    .field-group { display: flex; flex-direction: column; gap: 6px; }
    .field-group label { color: #cbd5e1; font-size: 13px; font-weight: 600; margin-left: 5px; }

    .input-box { position: relative; display: flex; align-items: center; }
    .input-box input {
        width: 100%; background: rgba(15, 23, 42, 0.5); border: 1px solid rgba(255, 255, 255, 0.1);
        padding: 13px 15px 13px 45px; border-radius: 16px; color: #fff; outline: none; transition: 0.3s;
    }
    .input-box input:focus { border-color: #10b981; background: rgba(15, 23, 42, 0.8); }

    .input-icon { position: absolute; left: 16px; color: #64748b; font-size: 16px; }
    .eye-btn { position: absolute; right: 15px; background: none; border: none; color: #64748b; cursor: pointer; }

    .password-hints { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 5px; margin-left: 5px; }
    .password-hints span { font-size: 10px; color: #475569; padding: 2px 8px; border-radius: 10px; border: 1px solid #475569; }
    .password-hints span.valid { color: #10b981; border-color: #10b981; background: rgba(16, 185, 129, 0.1); }

    .mismatch-text { color: #fb7185; font-size: 11px; margin-top: 4px; margin-left: 5px; }

    .reg-submit-btn {
        background: linear-gradient(135deg, #10b981, #059669); color: #fff; border: none;
        padding: 16px; border-radius: 16px; font-weight: 700; font-size: 15px;
        cursor: pointer; transition: 0.3s; display: flex; align-items: center; justify-content: center; gap: 10px;
        margin-top: 10px;
    }
    .reg-submit-btn:disabled { opacity: 0.4; cursor: not-allowed; }
    .reg-submit-btn:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 10px 20px rgba(16, 185, 129, 0.2); }

    .register-footer { margin-top: 25px; text-align: center; color: #94a3b8; font-size: 14px; }
    .register-footer a { color: #fff; font-weight: 700; text-decoration: none; margin-left: 5px; }

    .spinner { width: 20px; height: 20px; border: 2px solid rgba(255,255,255,0.3); border-radius: 50%; border-top-color: #fff; animation: spinReg 0.8s linear infinite; }
    @keyframes spinReg { to { transform: rotate(360deg); } }
`;

export default Register;